import React, { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Play, Eye, Trash2, AlertTriangle, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { adminPost, errMsg } from './api';

const API = process.env.REACT_APP_BACKEND_URL;

const OPERATIONS = [
  { value: 'find',       label: 'find',        destructive: false },
  { value: 'count',      label: 'count',       destructive: false },
  { value: 'deleteMany', label: 'deleteMany',  destructive: true  },
];

export default function QueryTab({ setup, onAfter }) {
  // Prefer `query_collections` (June 2026 — exposes `users` for member
  // delete/find ops, omits the virtual `customers` view which silently
  // returns 0). Falls back to the legacy `collections` list when the
  // backend hasn't been redeployed yet.
  const collections = (setup?.query_collections || setup?.collections || [])
    .map(c => c.collection);

  const [collection, setCollection] = useState('');
  const [operation, setOperation]   = useState('find');
  const [filterText, setFilterText] = useState('{}');
  const [filterValid, setFilterValid] = useState(true);
  const [limit, setLimit]           = useState(50);
  const [loading, setLoading]       = useState(false);
  const [result, setResult]         = useState(null);
  const [preview, setPreview]       = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [confirming, setConfirming]   = useState(false);

  const onFilterChange = useCallback((e) => {
    const val = e.target.value;
    setFilterText(val);
    try { JSON.parse(val); setFilterValid(true); }
    catch { setFilterValid(false); }
  }, []);

  const formatJSON = () => {
    try { setFilterText(JSON.stringify(JSON.parse(filterText), null, 2)); }
    catch { toast.error('Cannot format — invalid JSON'); }
  };

  const opDef   = OPERATIONS.find(o => o.value === operation);
  const canRun  = !!(collection && filterValid);

  const handleRun = async () => {
    if (!canRun) return;
    setLoading(true);
    setResult(null);
    setPreview(null);
    try {
      const filter = JSON.parse(filterText);
      if (operation === 'deleteMany') {
        const res = await adminPost(`${API}/api/admin/db/mutate/preview`, { collection, operation, filter });
        setPreview(res.data);
        setShowConfirm(true);
      } else {
        const res = await adminPost(`${API}/api/admin/db/query`, { collection, operation, filter, limit });
        setResult(res.data);
      }
    } catch (e) {
      toast.error(await errMsg(e, 'Query failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (confirmText.trim().toUpperCase() !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    setConfirming(true);
    try {
      const res = await adminPost(`${API}/api/admin/db/mutate/confirm`, {
        preview_token: preview.preview_token,
        confirm_text:  confirmText.trim(),
      });
      toast.success(`Deleted ${res.data.deleted} record(s) from ${collection}`);
      setShowConfirm(false);
      setPreview(null);
      setConfirmText('');
      setResult({ _meta: 'delete', deleted: res.data.deleted, collection });
      onAfter?.();
    } catch (e) {
      toast.error(await errMsg(e, 'Delete failed'));
    } finally {
      setConfirming(false);
    }
  };

  const cancelConfirm = () => {
    setShowConfirm(false);
    setPreview(null);
    setConfirmText('');
  };

  return (
    <div className="space-y-4">
      {/* ── Controls ────────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">

        <div className="grid grid-cols-2 gap-4">
          {/* Collection */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
              Collection
            </label>
            <select
              value={collection}
              onChange={e => { setCollection(e.target.value); setResult(null); }}
              data-testid="query-collection-select"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="" suppressHydrationWarning>Select collection…</option>
              {collections.map(c => <option key={c} value={c} suppressHydrationWarning>{c}</option>)}
            </select>
          </div>

          {/* Operation */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
              Operation
            </label>
            <div className="flex gap-2">
              {OPERATIONS.map(op => (
                <button
                  key={op.value}
                  type="button"
                  onClick={() => { setOperation(op.value); setResult(null); setPreview(null); }}
                  data-testid={`op-btn-${op.value}`}
                  className={`flex-1 py-2 rounded-lg text-xs font-bold border transition-colors ${
                    operation === op.value
                      ? op.destructive
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {op.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Filter textarea */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Filter (JSON)
            </label>
            <div className="flex items-center gap-3">
              {filterValid
                ? <span className="flex items-center gap-1 text-xs text-emerald-600"><CheckCircle className="w-3.5 h-3.5" /> Valid</span>
                : <span className="flex items-center gap-1 text-xs text-red-500"><XCircle className="w-3.5 h-3.5" /> Invalid JSON</span>}
              <button type="button" onClick={formatJSON}
                className="text-xs text-slate-400 hover:text-slate-700 underline">
                Format
              </button>
            </div>
          </div>
          <textarea
            value={filterText}
            onChange={onFilterChange}
            rows={6}
            spellCheck={false}
            data-testid="query-filter-input"
            placeholder={'{ "email": { "$regex": "@example\\.com$", "$options": "i" } }'}
            className={`w-full font-mono text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y ${
              filterValid ? 'border-slate-200' : 'border-red-400'
            }`}
          />
        </div>

        {/* Limit + Run */}
        <div className="flex items-center gap-3 justify-between">
          {operation === 'find' && (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <span className="text-xs font-semibold text-slate-500 uppercase">Limit</span>
              <select value={limit} onChange={e => setLimit(Number(e.target.value))}
                className="border border-slate-200 rounded-lg px-2 py-1 text-sm">
                {[10, 25, 50, 100, 200].map(n => <option key={n} value={n} suppressHydrationWarning>{n} rows</option>)}
              </select>
            </label>
          )}
          <div className="ml-auto flex items-center gap-2">
            {opDef?.destructive && (
              <span className="flex items-center gap-1 text-xs text-red-600 font-semibold">
                <AlertTriangle className="w-3.5 h-3.5" /> Preview required before delete
              </span>
            )}
            <button
              type="button"
              onClick={handleRun}
              disabled={!canRun || loading}
              data-testid="query-run-btn"
              className={`px-5 py-2 rounded-lg text-sm font-bold text-white transition-colors disabled:opacity-40 ${
                opDef?.destructive ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              {loading
                ? <><Loader2 className="w-4 h-4 inline mr-1 animate-spin" />Running…</>
                : opDef?.destructive ? <><Eye className="w-4 h-4 inline mr-1" />Preview Delete</>
                : operation === 'count' ? <><Play className="w-4 h-4 inline mr-1" />Count</>
                : <><Play className="w-4 h-4 inline mr-1" />Run Query</>}
            </button>
          </div>
        </div>
      </div>

      {/* ── Results ─────────────────────────────────────────────────── */}
      {result && <ResultsPanel result={result} operation={operation} collection={collection} />}

      {/* ── Confirm Delete dialog ────────────────────────────────────── */}
      {showConfirm && preview && (
        <ConfirmDeleteDialog
          preview={preview}
          confirmText={confirmText}
          setConfirmText={setConfirmText}
          confirming={confirming}
          onConfirm={handleConfirm}
          onCancel={cancelConfirm}
        />
      )}
    </div>
  );
}

/* ── Sub-components ────────────────────────────────────────────────────────── */

function ResultsPanel({ result, operation, collection }) {
  if (result._meta === 'delete') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
        <span className="text-sm font-semibold text-green-800">
          Deleted <strong>{result.deleted}</strong> record(s) from <code className="bg-green-100 px-1 rounded">{result.collection}</code>
        </span>
      </div>
    );
  }
  if (operation === 'count') {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
        <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">{collection} — count</p>
        <p className="text-6xl font-black text-emerald-600">{(result.count ?? 0).toLocaleString()}</p>
        <p className="text-xs text-slate-400 mt-1">matching documents</p>
      </div>
    );
  }
  const rows = result.rows || [];
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <span className="text-sm font-semibold text-slate-700">Results — {collection}</span>
        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-mono">{rows.length} row{rows.length !== 1 ? 's' : ''}</span>
      </div>
      {rows.length === 0
        ? <p className="text-sm text-slate-500 px-5 py-6 text-center">No documents matched the filter.</p>
        : <pre className="text-xs font-mono p-4 overflow-x-auto overflow-y-auto max-h-96 text-slate-700 bg-slate-50 leading-relaxed">{JSON.stringify(rows, null, 2)}</pre>}
    </div>
  );
}

function ConfirmDeleteDialog({ preview, confirmText, setConfirmText, confirming, onConfirm, onCancel }) {
  const ready = confirmText.trim().toUpperCase() === 'DELETE';
  return (
    <div className="bg-red-50 border-2 border-red-300 rounded-xl p-5 space-y-4" data-testid="confirm-delete-dialog">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-bold text-red-800">
            About to permanently delete <strong>{preview.affected_count}</strong> record{preview.affected_count !== 1 ? 's' : ''} from{' '}
            <code className="bg-red-100 px-1 rounded">{preview.collection}</code>
          </p>
          <p className="text-xs text-red-600 mt-0.5">This cannot be undone. Export a backup first if needed.</p>
        </div>
      </div>

      {(preview.sample || []).length > 0 && (
        <div>
          <p className="text-xs font-semibold text-red-700 uppercase mb-1">Sample of records that will be deleted</p>
          <pre className="text-xs font-mono bg-white border border-red-200 rounded-lg p-3 overflow-x-auto max-h-40 text-slate-700">
            {JSON.stringify(preview.sample, null, 2)}
          </pre>
        </div>
      )}

      <div>
        <label className="block text-sm font-semibold text-red-800 mb-1">
          Type <code className="bg-red-100 px-1 rounded">DELETE</code> to confirm
        </label>
        <input
          type="text"
          value={confirmText}
          onChange={e => setConfirmText(e.target.value)}
          placeholder="DELETE"
          data-testid="confirm-delete-input"
          className="w-full border border-red-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
        />
      </div>

      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel}
          className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50">
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={!ready || confirming}
          data-testid="confirm-delete-btn"
          className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-40 transition-colors"
        >
          {confirming
            ? <><Loader2 className="w-4 h-4 inline mr-1 animate-spin" />Deleting…</>
            : <><Trash2 className="w-4 h-4 inline mr-1" />Delete {preview.affected_count} Records</>}
        </button>
      </div>
    </div>
  );
}
